import React, { useState } from 'react';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EmptyStateLayout, LoadingIcon } from '@panwds/react-ui';
import { useTableInstance } from '../TableContext/TableContext.esm.js';
import '../TableContext/TableProvider.esm.js';
import { Body } from './Body.esm.js';
import { Footer } from './Footer.esm.js';
import { FooterRow } from './FooterRow.esm.js';
import { Header } from './Header.esm.js';
import { HeaderRow } from './HeaderRow.esm.js';
import { SortableRow, Row } from './Row.esm.js';
import { Table } from './Table.esm.js';

/**
 * Renders the table.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { ConnectedTable, TableProvider } from "@panwds/react-table";
 * ```
 */
function ConnectedTable({ appearance = "primary", headerstyle, enableEmptyState = true, emptyStateProps = {}, shouldHighlightRow, shouldDisableRow, showFooter = false, dataTestId, dataMetrics, showHeader = true, uppercaseHeaderColumns = false, rowSize = "standard", }) {
    const { disableColumnOrder, disablePagination, footerGroups, getTableProps, headerGroups, isLoading, setColumnOrder, getTableBodyProps, prepareRow, page, onRowSortEnd, rows, } = useTableInstance();
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const visibleRows = disablePagination ? rows : page;
    const activeRow = activeId && rows.find((row) => row.id === activeId);
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { "data-metrics": dataMetrics, "data-testid": dataTestId || "connected-table", ...getTableProps() },
            showHeader && (React.createElement(Header, { headerstyle: headerstyle }, headerGroups.map((headerGroup, i) => (React.createElement(HeaderRow, { appearance: appearance, uppercaseHeaderColumns: uppercaseHeaderColumns, disableColumnOrder: disableColumnOrder, headerGroup: headerGroup, setColumnOrder: setColumnOrder, key: i }))))),
            React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: function handleDragStart(event) {
                    const { active } = event;
                    setActiveId(active.id);
                }, onDragEnd: function handleDragEnd(event) {
                    const { active, over } = event;
                    if (active.id !== (over === null || over === void 0 ? void 0 : over.id) && onRowSortEnd) {
                        const oldIndex = visibleRows.findIndex((row) => String(row.id) === active.id);
                        const newIndex = visibleRows.findIndex((row) => String(row.id) === (over === null || over === void 0 ? void 0 : over.id));
                        const sortedData = arrayMove(visibleRows, oldIndex, newIndex).map((row) => row.original);
                        onRowSortEnd({
                            sortedData,
                            activeId: active.id,
                            overId: over === null || over === void 0 ? void 0 : over.id,
                        });
                    }
                    setActiveId(null);
                }, modifiers: [restrictToVerticalAxis, restrictToParentElement] },
                React.createElement(SortableContext, { items: visibleRows, strategy: verticalListSortingStrategy },
                    React.createElement(Body, { ...getTableBodyProps() }, visibleRows.map((row) => {
                        prepareRow(row);
                        return (React.createElement(SortableRow, { appearance: appearance, rowSize: rowSize, shouldHighlightRow: shouldHighlightRow, shouldDisableRow: shouldDisableRow, row: row, key: row.id }));
                    }))),
                React.createElement(DragOverlay, { zIndex: 20 }, activeRow ? (React.createElement(Row, { row: activeRow, appearance: appearance, rowSize: rowSize, shouldHighlightRow: shouldHighlightRow, shouldDisableRow: shouldDisableRow, isHeld: true })) : null)),
            showFooter && (React.createElement(Footer, null, footerGroups.map((footerGroup, i) => (React.createElement(FooterRow, { footerGroup: footerGroup, appearance: appearance, key: i })))))),
        enableEmptyState && rows.length === 0 && !isLoading && (React.createElement(EmptyStateLayout, { ...emptyStateProps })),
        isLoading && (React.createElement("div", { className: "tw-flex tw-items-center tw-justify-center tw-h-full" },
            React.createElement(LoadingIcon, { size: "3xl", className: "tw-animate-spin" })))));
}

export { ConnectedTable };
//# sourceMappingURL=ConnectedTable.esm.js.map
